/**
 * TRIAGE — the verdict, and what to look at first.
 *
 * WHY THIS EXISTS. The board answered "what is the state of everything" and
 * left "what is wrong" to be assembled by eye from eleven panels. That is the
 * wrong order for the four minutes after a pager fires, and it is how the
 * normal video band sat at 112,375 undelivered with zero movement for half an
 * hour while every panel on the page rendered correctly. Nothing was broken
 * about the panels; the reader simply had to already know where to look.
 *
 * IT DERIVES, IT DOES NOT FETCH. Every finding below comes from the same
 * react-query hooks the panels themselves use, so the cache is shared, no
 * request is added, and the verdict at the top of the page CANNOT disagree with
 * the panel that renders the same number further down. A triage list assembled
 * from its own endpoint would eventually contradict the board and would then be
 * worse than no triage list at all.
 *
 * NULL IS NOT ZERO. Both api/schemas.ts and the throughput schema say this
 * explicitly, and it is the rule that decides whether this component is honest.
 * Null means the server cannot state a rate yet -- a first sample, or a counter
 * reset by a deploy -- and a null is never reported as a fault. The same applies
 * to `workers`: 0 is a wedged replica, null is an unreadable Redis.
 *
 * AND ZERO IS NOT ALWAYS A STALL. This panel shipped believing the converse,
 * and was wrong within the hour: a stream's `rate_per_s === 0` raised a CRIT on
 * both video bands while the worker was completing 6.3/s and both backlogs were
 * falling. The queue rate comes from Redis `entries-read`, which advances one
 * 256-entry claim chunk at a time, so a sample landing between claims reads
 * exactly zero on a perfectly healthy consumer. Rates are now taken only from
 * the workers' own completed-work counters, and only at pipeline level -- see
 * the stall rule below for the measurement that settled it.
 *
 * SCORING REPORTS ITSELF HERE TOO, WITHOUT BEING DERIVED HERE. The collector
 * computes scoring's findings once, server-side, for the Scoring tab, the rail,
 * the alert email and this band alike, so this file adds no scoring threshold:
 * `scoringFindings` below only carries the server's crit/warn findings across
 * (info stays on the tab) and says so when it cannot read them. It is kept apart
 * from `deriveFindings` -- which stays a pure function of the collector and
 * classifier payloads -- and the component merges the two lists BY RANK (see
 * `mergeByRank`), never by a weight shared between them.
 */
import {
  useHealth, useQueues, useClassifierQueues, useThroughput, useClassifierThroughput,
  useScoringOverview, useScoringOutcomes, useScoringJobs, useScoringFailures,
} from '../api/queries'
import type {
  ScoringFailures, ScoringJobs, ScoringOutcomes, ScoringOverview,
} from '../api/schemas'
import { PanelState } from '../components/ui'
import { n } from '../components/format'
import { scoringReads } from './scoringReads'
import type { Read, ScoringReadKey, ScoringReading } from './scoringReads'
import './Triage.css'

export type Severity = 'crit' | 'warn'

export type Finding = {
  id: string
  sev: Severity
  /** What is wrong, in the operator's words. */
  title: string
  /** The mechanism -- why this happens, so the reader can act without digging. */
  why: string
  /** The number that makes it real. */
  value: string
  /** Sorts within a severity. Bigger is worse. */
  weight: number
}

type Health = ReturnType<typeof useHealth>['data']
type Queues = ReturnType<typeof useQueues>['data']
type ClsQueues = ReturnType<typeof useClassifierQueues>['data']
type Tp = ReturnType<typeof useThroughput>['data']

const short = (s: string) => s.replace(/^classifier:/, '')

/** Worst first: critical before warning, then the bigger weight. */
const worstFirst = (a: Finding, b: Finding) =>
  a.sev === b.sev ? b.weight - a.weight : a.sev === 'crit' ? -1 : 1

/**
 * The whole of the logic, as a pure function of already-fetched data.
 *
 * Kept separate from the component so it can be tested against real payload
 * shapes without mounting React or mocking a query client -- the rules here are
 * the part that can be wrong in a way that matters.
 */
export function deriveFindings(
  health: Health, queues: Queues, cls: ClsQueues, tp?: Tp, ctp?: Tp,
): Finding[] {
  const out: Finding[] = []

  // -- a role with no live worker ------------------------------------------
  // consumers > 0 means the group exists and work is addressed to it; alive 0
  // means nothing is beating. A group with no consumers at all is simply not
  // deployed, which is not a fault.
  for (const r of health?.roles ?? []) {
    if (r.alive === 0 && r.consumers > 0) {
      out.push({
        id: `role:${r.group}`, sev: 'crit',
        title: `${r.group} has no live worker`,
        why: `${r.consumers} consumer${r.consumers === 1 ? '' : 's'} registered on ${short(r.stream)}, none beating`,
        value: '0 alive', weight: 1e9,
      })
    }
  }

  // -- the classifier's own heartbeat --------------------------------------
  // Zero is a real answer and means wedged. Null means the classifier Redis
  // could not be read, which is a different fault and is not claimed here.
  for (const [role, count] of Object.entries(cls?.workers ?? {})) {
    if (count === 0) {
      out.push({
        id: `worker:${role}`, sev: 'crit',
        title: `classifier ${role} is not running`,
        why: 'no heartbeat from any replica of this role',
        value: '0 replicas', weight: 9e8,
      })
    }
  }

  // -- queued work with nothing completing ----------------------------------
  //
  // THE RATE MUST COME FROM THE WORKERS, NOT FROM THE QUEUE. A first version of
  // this rule read `rate_per_s` off each stream and called `=== 0` a stall. It
  // fired a CRIT on videos.classify.norm and .high on 2026-08-04 while the
  // worker was completing 6.3/s and both backlogs were falling -- measured over
  // 90s, high dropped 1,024 and norm 256. Those deltas are exact multiples of
  // the 256-entry claim chunk, which is the whole explanation: the queue's rate
  // derives from Redis `entries-read`, that counter advances one chunk at a
  // time, and any sample landing between two claims reads exactly 0. Zero is a
  // NORMAL reading for a healthy chunked consumer, so it cannot mean stalled.
  // The Throughput schema says as much in its own words -- the per-role counter
  // exists precisely because `entries-read` misreports (213/s against a true
  // 0.69/s on 2026-08-02).
  //
  // So the claim is made at PIPELINE level from each worker's own completed-work
  // counter, and only when there is a real measurement to stand on: at least one
  // non-null rate, and every non-null rate zero. All-null means "no baseline
  // yet" and says nothing, which is the same null-is-not-zero rule as everywhere
  // else here.
  //
  // What this deliberately does NOT do is detect one band starving behind
  // another while the pipeline as a whole is busy -- the original norm bug. That
  // needs a lag delta across two samples, and this board is pull-on-demand with
  // no history, so a point-in-time reading cannot tell "starved" from "between
  // claims". Better to stay silent than to guess: a CRIT that cries wolf is the
  // exact failure this panel was built to remove.
  const rates = (roles: Record<string, { per_s: number | null }> | undefined) =>
    Object.values(roles ?? {}).map((r) => r.per_s).filter((v): v is number => v !== null)

  const stalled = (
    label: string,
    streams: ReadonlyArray<{ lag: number | null }> | undefined,
    roles: Record<string, { per_s: number | null }> | undefined,
  ) => {
    const queued = (streams ?? []).reduce((a, s) => a + (s.lag ?? 0), 0)
    const measured = rates(roles)
    if (queued > 0 && measured.length > 0 && measured.every((v) => v === 0)) {
      out.push({
        id: `stall:${label}`, sev: 'crit',
        title: `${label} has queued work and nothing completing`,
        why: 'every worker reports a completed-work rate of zero',
        value: `${n(queued)} waiting`, weight: queued,
      })
    }
  }
  stalled('collector', queues?.streams, tp?.roles)
  stalled('classifier', cls?.streams, ctp?.roles)

  // -- consumer registrations outliving their process ----------------------
  // A consumer group cannot legitimately hold more consumers than there are
  // live worker processes: each worker registers once per stream it reads. So
  // consumers > total live workers is proof of stale registrations, with no
  // stream-to-role mapping needed -- that mapping is the brittle part, and this
  // bound avoids it entirely. Only claimed when both numbers are known.
  //
  // CONSUMERS ARE THE CLASSIFY WORKERS. The feeder publishes and never joins a
  // group, so summing every role overstated "live" and understated "stale".
  const live = cls?.workers?.classify ?? null
  if (live !== null && live > 0) {
    for (const s of cls?.streams ?? []) {
      if (s.consumers !== null && s.consumers > live) {
        out.push({
          id: `stale:${s.stream}`, sev: 'warn',
          title: `${short(s.stream)} has stale consumers`,
          why: `${s.consumers} registered against ${live} live worker${live === 1 ? '' : 's'} — leftovers from restarts`,
          value: `${s.consumers - live} stale`, weight: s.consumers - live,
        })
      }
    }
  }

  // -- dead letters ---------------------------------------------------------
  // An old pile is not an alert: the 1,098 entries from August sat here as a WARN
  // for three months, on a list whose whole value is that everything on it is new.
  // An unknown age keeps the entry -- older collectors do not report one.
  const dlqAge = health?.dead_letter_newest_age_s
  if (health?.dead_letter && (dlqAge == null || dlqAge < 7 * 86_400)) {
    out.push({
      id: 'dlq', sev: 'warn',
      title: 'messages in the dead-letter queue',
      why: 'work that exhausted its retries and will not be attempted again',
      value: n(health.dead_letter), weight: health.dead_letter,
    })
  }

  // -- the daily LLM budget, at 90% ----------------------------------------
  // Exhausting it is what caused the 11-hour stall, and the useful moment to
  // say so is before it runs out rather than after.
  if (cls?.llm && cls.llm.cap > 0) {
    const pct = cls.llm.used / cls.llm.cap
    if (pct >= 0.9) {
      out.push({
        id: 'llm', sev: 'warn',
        title: 'LLM budget nearly spent',
        why: 'classification stops when the daily cap is reached',
        value: `${Math.round(pct * 100)}% of ${n(cls.llm.cap)}`, weight: 1e6,
      })
    }
  }

  // -- whatever the server itself reports and we have not already said ------
  // The API states its own problems; those are authoritative. Dead letters are
  // dropped because the rule above already renders that number with its cause.
  for (const p of health?.problems ?? []) {
    if (/dead-?letter/i.test(p)) continue
    out.push({
      id: `srv:${p}`, sev: 'warn', title: p,
      why: 'reported by the collector', value: '', weight: 1,
    })
  }

  return out.sort(worstFirst)
}

/* ------------------------------------------------------------------ scoring */

/** What a blind read says, by endpoint. Ruling R10, matching the rail: an
 *  unreadable /overview is crit (it carries the freshness and liveness of every
 *  stage -- blind is not healthy); an unreadable secondary read is a warn, the
 *  severity the collector itself gives an unreadable source. */
const BLIND: Record<ScoringReadKey, { sev: Severity; title: string; why: string }> = {
  overview: {
    sev: 'crit', title: 'Scoring metrics could not be read',
    why: 'liveness and freshness of every scoring stage come from this read -- blind is not healthy',
  },
  outcomes: {
    sev: 'warn', title: 'Scoring outcomes could not be read',
    why: 'score-mix, data-age and tier-shift checks cannot run while it fails -- blind is not healthy',
  },
  jobs: {
    sev: 'warn', title: 'Scoring jobs could not be read',
    why: 'stuck and failed CSV scoring jobs cannot be seen while it fails -- blind is not healthy',
  },
  failures: {
    sev: 'warn', title: 'Scoring failures could not be read',
    why: 'poison channels in the failure ring cannot be seen while it fails -- blind is not healthy',
  },
}

/** One endpoint's contribution: its findings, or -- if the read failed -- the
 *  one finding that says so. */
function fromReading(r: ScoringReading) {
  if (r.unread) {
    const b = BLIND[r.key]
    return [{ id: 'unread', sev: b.sev, title: b.title, why: b.why, value: r.path }]
  }
  return r.findings
}

/**
 * Scoring's findings for the verdict band, as a pure function of the four reads
 * the Scoring tab already makes (no extra fetch). `deriveFindings` is untouched;
 * the component merges the two lists with `mergeByRank`.
 *
 *  - crit and warn only, from /overview, /outcomes, /jobs, /failures. Title, why
 *    and value are the server's words.
 *  - still loading contributes nothing: no false finding before Load.
 *  - a failed read is one finding that names it (see BLIND), and its previous
 *    data is not shown -- `scoringReads` is the rail's own judgement of that, so
 *    the dot and this band cannot disagree.
 *  - worst first: crit above warn, then endpoint order (overview, outcomes,
 *    jobs, failures), then the server's own order within an endpoint. `weight`
 *    carries exactly that order (N, N-1, ... 1) and means nothing outside this
 *    list -- the server states a severity but no magnitude, so there is no
 *    honest number to put beside a queue depth, and nothing compares it to one.
 *  - ids are opaque to the board and only namespaced here (`scoring:<endpoint>:`)
 *    so they are unique keys beside the other pipelines' findings; nothing
 *    parses them or builds a DOM id or selector from them.
 */
export function scoringFindings(
  overview: Read<ScoringOverview>, outcomes: Read<ScoringOutcomes>,
  jobs: Read<ScoringJobs>, failures: Read<ScoringFailures>,
): Finding[] {
  const found: Array<Omit<Finding, 'weight'>> = []
  for (const r of scoringReads(overview, outcomes, jobs, failures)) {
    for (const f of fromReading(r)) {
      if (f.sev === 'info') continue   // stays on the tab; never reaches Triage
      found.push({
        id: `scoring:${r.key}:${f.id}`, sev: f.sev, title: f.title, why: f.why, value: f.value,
      })
    }
  }
  // Endpoint order is already the order of `found`; a stable partition puts the
  // crits first without disturbing it.
  const ordered = [...found.filter((f) => f.sev === 'crit'), ...found.filter((f) => f.sev === 'warn')]
  return ordered.map((f, i) => ({ ...f, weight: ordered.length - i }))
}

/**
 * One list from several, by rank -- never by a weight the sources share.
 *
 * Each source's own weights mean something only inside that source: a
 * dead-letter count of 791,394 and a scoring finding's position are not
 * quantities on one scale, and inventing a scale to compare them would let one
 * constant decide which pipeline leads the verdict. So each source keeps the
 * order it already has (every list passed in is worst-first by its own rules)
 * and the merge compares only:
 *
 *   1. severity -- every crit before any warn;
 *   2. rank within the finding's own source -- its 0-based position among that
 *      source's findings of the same severity, so each source's worst leads and
 *      neither can crowd the other out;
 *   3. source order -- on a tie, the sources as listed (the existing pipelines
 *      before scoring).
 *
 * With one non-empty source this is the identity, so a quiet scoring leaves the
 * order exactly as it was before scoring joined the band.
 */
export function mergeByRank(...sources: ReadonlyArray<readonly Finding[]>): Finding[] {
  const keyed = sources.flatMap((list, src) => {
    const seen: Record<Severity, number> = { crit: 0, warn: 0 }
    return list.map((f) => ({ f, src, rank: seen[f.sev]++ }))
  })
  return keyed
    .sort((a, b) =>
      (a.f.sev === b.f.sev ? 0 : a.f.sev === 'crit' ? -1 : 1) || a.rank - b.rank || a.src - b.src)
    .map((k) => k.f)
}

/** One finding as a row of the band. */
function FindingRow({ f }: { f: Finding }) {
  return (
    <li className="tri-row">
      <span className={`tri-sev s-${f.sev}`}>{f.sev === 'crit' ? 'CRIT' : 'WARN'}</span>
      <span className="tri-txt">
        <b>{f.title}</b>
        <span>{f.why}</span>
      </span>
      {f.value && <span className="tri-v tnum">{f.value}</span>}
    </li>
  )
}

export function Triage() {
  const health = useHealth()
  const queues = useQueues()
  const cls = useClassifierQueues()
  const tp = useThroughput()
  const ctp = useClassifierThroughput()
  // The Scoring tab's own cached reads (the rail makes the same four), so a
  // scoring finding reaches the verdict from any tab at no extra request. They
  // are not part of `isLoading` below: a read that has not answered yet
  // contributes nothing, and a failed one is itself a finding.
  const scOverview = useScoringOverview()
  const scOutcomes = useScoringOutcomes()
  const scJobs = useScoringJobs()
  const scFailures = useScoringFailures()

  const isLoading = health.isLoading || queues.isLoading || cls.isLoading
  // Only the health query is required. The queue endpoints degrade to "no
  // findings from this source" rather than blanking the verdict, because a
  // board that hides its summary when one panel fails is the failure mode this
  // component was written to remove.
  const error = health.error

  // Scoring's findings are computed once and used twice: merged into the verdict
  // below, and on their own when health is down (the collector's share cannot
  // be read then, but Scoring is a different pipeline and still reports itself).
  const scoring = scoringFindings(scOverview, scOutcomes, scJobs, scFailures)
  const findings = mergeByRank(
    deriveFindings(health.data, queues.data, cls.data, tp.data, ctp.data), scoring)
  // "Clear" is a claim about every pipeline, so it waits until all four scoring
  // reads have answered (a failure is an answer -- it is a finding). Until then
  // the board is neither clear nor accused: it says it is still waiting.
  const scoringSettled = [scOverview, scOutcomes, scJobs, scFailures].every((q) => !q.isPending)
  const clear = findings.length === 0 && scoringSettled
  const waiting = findings.length === 0 && !scoringSettled
  const crit = findings.filter((f) => f.sev === 'crit').length

  return (
    <section className={`triage${clear ? ' triage-clear' : ''}`} aria-label="Needs attention">
      <PanelState isLoading={isLoading} error={error}>
        <div className="tri-verdict">
          <span className={`tri-n${crit ? ' crit' : findings.length ? ' warn' : ''}`}>
            {clear ? '✓' : waiting ? '…' : findings.length}
          </span>
          <div className="tri-lead">
            <h2>
              {clear ? 'Everything is moving'
                : waiting ? 'Scoring has not answered yet'
                : findings.length === 1 ? 'One thing needs attention'
                : `${findings.length} things need attention`}
            </h2>
            <p>
              {clear
                ? 'No stalled streams, no dead letters, every role beating.'
                : waiting
                  ? 'Nothing else needs attention, but the board is not called clear until it does.'
                  : findings[0]!.title.charAt(0).toUpperCase() + findings[0]!.title.slice(1)
                    + (findings.length > 1 ? ` · and ${findings.length - 1} more` : '')}
            </p>
          </div>
        </div>

        {findings.length > 0 && (
          <ul className="tri-list">
            {findings.map((f, i) => (
              // Index + id: ids are unique per source, not guaranteed across
              // them, and the verdict must render every finding it counts.
              <FindingRow key={`${i}:${f.id}`} f={f} />
            ))}
          </ul>
        )}
      </PanelState>
      {/* Health is down: PanelState has said so and nothing else, but Scoring does
        * not depend on it. Its rows follow the unavailable line with no total
        * above them -- a headline count would claim a sum this board cannot
        * compute without the collector's share. */}
      {error && scoring.length > 0 && (
        <ul className="tri-list" aria-label="Scoring findings">
          {scoring.map((f, i) => <FindingRow key={`${i}:${f.id}`} f={f} />)}
        </ul>
      )}
    </section>
  )
}
